import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Video, MapPin, Clock, User as UserIcon, LinkIcon, Users, CalendarDays,
  CheckCircle2, AlertTriangle, Rocket, ChevronLeft, Store as StoreIcon,
} from 'lucide-react';
import { onboardingAPI } from '../services/api';
import { InlineLoader } from './loading';

/**
 * "Initiate Onboarding Batch" — turns the Journey template into a running
 * schedule for a set of new joiners.
 *
 *  Step 1: confirm the activities and enter the meeting URL for each online one
 *          (pre-filled from the template; in-person + shadow days need none).
 *  Step 2: confirm the joiners (pre-selected, deselectable) + their auto-buddy.
 *
 * Saving records the batch and materialises each non-shadow activity as a real
 * Offline Training so attendance is captured there.
 */

interface BatchActivity {
  sessionId: number;
  title: string;
  fromDay: number;
  toDay: number;
  startTime: string | null;
  durationMinutes: number | null;
  host: string | null;
  mode: string;
  meetingLink: string | null;
  needsUrl: boolean;
}

interface BatchJoiner {
  employeeCode: string;
  employeeName: string | null;
  dateOfJoining: string | null;
  buddyCode: string | null;
  buddyName: string | null;
  buddyStoreName: string | null;
  hasCityBuddyMatch: boolean;
}

interface BatchPreview {
  journeyDays: number;
  activities: BatchActivity[];
  joiners: BatchJoiner[];
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Day 1 = the joining day (1-based display); fromDay/toDay are 0-based.
const dayLabel = (a: BatchActivity): string =>
  a.fromDay === a.toDay ? `Day ${a.fromDay + 1}` : `Day ${a.fromDay + 1}–${a.toDay + 1}`;

const fmtDuration = (mins: number | null): string => {
  if (!mins || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? (m > 0 ? `${h} hr ${m} min` : `${h} hr`) : `${m} min`;
};

export default function OnboardingBatchModal({
  projectId, onClose, onDone,
}: {
  projectId: number;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await onboardingAPI.getBatchPreview(projectId);
        const data = (res.data?.data as BatchPreview) ?? null;
        if (cancelled) return;
        setPreview(data);
        // Seed URLs from the template's standing links, and pre-select everyone.
        const seededUrls: Record<string, string> = {};
        (data?.activities ?? []).forEach((a) => { if (a.needsUrl) seededUrls[String(a.sessionId)] = a.meetingLink ?? ''; });
        setUrls(seededUrls);
        setSelected(new Set((data?.joiners ?? []).map((j) => j.employeeCode)));
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Failed to load the batch preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const onlineActivities = useMemo(() => (preview?.activities ?? []).filter((a) => a.needsUrl), [preview]);

  const goToPeople = () => {
    // Online activities require a meeting URL (WebEx etc.).
    const missing = onlineActivities.filter((a) => !(urls[String(a.sessionId)] || '').trim());
    if (missing.length > 0) {
      setUrlError(`Enter a meeting URL for: ${missing.map((a) => a.title).join(', ')}.`);
      return;
    }
    setUrlError(null);
    setStep(2);
  };

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const handleInitiate = async () => {
    if (!preview) return;
    if (selected.size === 0) { setSaveError('Select at least one joiner.'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const meetingUrls: Record<string, string> = {};
      onlineActivities.forEach((a) => { meetingUrls[String(a.sessionId)] = (urls[String(a.sessionId)] || '').trim(); });
      const res = await onboardingAPI.initiateBatch({
        projectId,
        employeeCodes: [...selected],
        meetingUrls,
      });
      const m = res.data?.meta ?? {};
      onDone(`Onboarding plan initiated for ${m.members ?? selected.size} joiner${(m.members ?? selected.size) === 1 ? '' : 's'} · ${m.trainingsCreated ?? 0} activities scheduled.`);
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to initiate the batch.');
      setSaving(false);
    }
  };

  const activities = preview?.activities ?? [];
  const joiners = preview?.joiners ?? [];

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4 py-10" role="dialog" aria-modal="true">
      <div className="absolute inset-0" onClick={() => !saving && onClose()} />
      <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50">
              <Rocket className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Initiate onboarding batch</h3>
              <p className="text-xs text-gray-500">
                {step === 1 ? 'Step 1 of 2 · Confirm activities & meeting links' : 'Step 2 of 2 · Choose the new joiners'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="py-16 text-center text-sm text-gray-400"><InlineLoader label="Loading the onboarding plan…" /></div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : activities.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-12 text-center">
              <p className="text-sm font-medium text-gray-700">No activities to schedule</p>
              <p className="mt-1 text-xs text-gray-500">Add non-shadow activities to the Journey first, then initiate a batch.</p>
            </div>
          ) : step === 1 ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Every activity below will be scheduled as a training for each joiner (on their own calendar). Enter the meeting link for the online ones — in-person days and the shadow week need none.
              </p>
              {activities.map((a) => {
                const online = a.needsUrl;
                return (
                  <div key={a.sessionId} className="rounded-xl border border-gray-200 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-primary-50 px-2 py-0.5 text-[11px] font-bold text-primary-700">{dayLabel(a)}</span>
                      <span className="text-sm font-semibold text-gray-900">{a.title}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${online ? 'bg-sky-50 text-sky-700' : 'bg-gray-100 text-gray-600'}`}>
                        {online ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                        {online ? 'Online' : 'In person'}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                      {a.startTime && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{a.startTime}{fmtDuration(a.durationMinutes) ? ` · ${fmtDuration(a.durationMinutes)}` : ''}</span>}
                      {a.host && <span className="flex items-center gap-1"><UserIcon className="h-3 w-3" />{a.host}</span>}
                    </div>
                    {online ? (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
                          <LinkIcon className="h-4 w-4 shrink-0 text-gray-400" />
                          <input
                            type="url"
                            value={urls[String(a.sessionId)] ?? ''}
                            onChange={(e) => setUrls((p) => ({ ...p, [String(a.sessionId)]: e.target.value }))}
                            placeholder="https://…  (WebEx / meeting link)"
                            className="w-full text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1.5 text-xs text-gray-400">No meeting link needed.</p>
                    )}
                  </div>
                );
              })}
              {urlError && (
                <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{urlError}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">New joiners from the last 15 days who don't yet have a plan. Deselect anyone you want to leave out.</p>
                <span className="shrink-0 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-semibold text-primary-700">{selected.size} of {joiners.length} selected</span>
              </div>
              {joiners.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-12 text-center">
                  <p className="text-sm font-medium text-gray-700">No eligible joiners</p>
                  <p className="mt-1 text-xs text-gray-500">Everyone recent already has a plan, or no one joined in the last 15 days.</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200">
                  <ul className="divide-y divide-gray-100">
                    {joiners.map((j) => {
                      const on = selected.has(j.employeeCode);
                      return (
                        <li key={j.employeeCode}>
                          <button type="button" onClick={() => toggle(j.employeeCode)} className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors ${on ? 'bg-primary-50/50' : 'hover:bg-gray-50'}`}>
                            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${on ? 'border-primary-600 bg-primary-600' : 'border-gray-300'}`}>
                              {on && <CheckCircle2 className="h-3 w-3 text-white" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">{j.employeeName || j.employeeCode}</span>
                                <span className="text-xs text-gray-400">{j.employeeCode}</span>
                              </span>
                              <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />Joined {fmtDate(j.dateOfJoining)}</span>
                                {j.buddyName || j.buddyCode ? (
                                  <span className="flex items-center gap-1 text-gray-600">
                                    <StoreIcon className="h-3 w-3" />Buddy: <strong className="font-semibold">{j.buddyName || j.buddyCode}</strong>
                                    {j.buddyStoreName ? ` · ${j.buddyStoreName}` : ''}
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">No buddy yet — set on dashboard</span>
                                )}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {saveError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{saveError}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && activities.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
            {step === 1 ? (
              <>
                <p className="text-xs text-gray-500 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{joiners.length} joiner{joiners.length === 1 ? '' : 's'} eligible</p>
                <button type="button" onClick={goToPeople} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700">
                  Next: choose people →
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => setStep(1)} disabled={saving} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50">
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
                <button type="button" onClick={handleInitiate} disabled={saving || selected.size === 0} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300">
                  {saving ? 'Initiating…' : `Initiate for ${selected.size} joiner${selected.size === 1 ? '' : 's'}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
